import { ProviderTemplate, AttributeMapping, ProviderMetadata } from '@/types/sso.types';

// Default attribute mapping for OpenID Connect providers
const defaultAttributeMapping: AttributeMapping = {
  email: 'email',
  given_name: 'given_name',
  family_name: 'family_name',
  picture: 'picture',
  sub: 'sub',
};

// Provider templates for common SSO providers
export const providerTemplates: Record<string, ProviderTemplate> = {
  google: {
    provider_name: 'google',
    display_name: 'Sign in with Google',
    logo_url: '/assets/logos/google-logo.svg',
    openid_wellknown_url: 'https://accounts.google.com/.well-known/openid-configuration',
    scope: 'openid profile email',
    attribute_mapping: defaultAttributeMapping,
    additional_params: {
      access_type: 'offline',
      prompt: 'consent',
    },
    metadata: {
      provider_type: 'google',
      company: 'Google Inc',
      support_url: 'https://support.google.com',
    },
    instructions: `
**Google OAuth Setup Instructions:**

1. Go to the [Google Cloud Console](https://console.cloud.google.com/)
2. Select or create a project
3. Go to "APIs & Services" → "Credentials"
4. Click "Create Credentials" → "OAuth 2.0 Client ID"
5. Configure the consent screen if not done already
6. Choose "Web application" as the application type
7. Add your authorized redirect URI (auto-generated above - copy it!)
8. Copy the Client ID and Client Secret

**Required Scopes:** openid, profile, email
**Note:** The redirect URI is automatically generated in the format:
\`{API_BASE_URL}/api/auth/sso/{provider_name}/callback\`
    `.trim(),
  },

  microsoft: {
    provider_name: 'microsoft',
    display_name: 'Sign in with Microsoft',
    logo_url: '/assets/logos/microsoft-logo.svg',
    openid_wellknown_url: 'https://login.microsoftonline.com/common/v2.0/.well-known/openid-configuration',
    scope: 'openid profile email',
    attribute_mapping: defaultAttributeMapping,
    additional_params: {
      tenant: 'common',
      prompt: 'select_account',
    },
    metadata: {
      provider_type: 'microsoft',
      company: 'Microsoft Corporation',
      tenant_type: 'common',
      support_url: 'https://docs.microsoft.com/azure/active-directory',
    },
    instructions: `
**Microsoft Azure AD Setup Instructions:**

1. Go to the [Azure Portal](https://portal.azure.com/)
2. Navigate to "Azure Active Directory" → "App registrations"
3. Click "New registration"
4. Enter application name and select supported account types
5. Add your authorized redirect URI (auto-generated above - copy it!)
6. After creation, go to "Certificates & secrets"
7. Create a new client secret and copy the value
8. Go to "API permissions" and add:
   - Microsoft Graph → Delegated → openid
   - Microsoft Graph → Delegated → profile  
   - Microsoft Graph → Delegated → email
9. Copy the Application (client) ID from the Overview page

**Tenant Configuration:**
- Use "common" for multi-tenant (personal + work accounts)
- Use your tenant ID for single tenant
- Use "organizations" for work/school accounts only
    `.trim(),
  },

  okta: {
    provider_name: 'okta',
    display_name: 'Sign in with Okta',
    logo_url: '/assets/logos/okta-logo.svg',
    openid_wellknown_url: 'https://your-domain.okta.com/.well-known/openid-configuration',
    scope: 'openid profile email',
    attribute_mapping: defaultAttributeMapping,
    additional_params: {
      response_mode: 'query',
    },
    metadata: {
      provider_type: 'okta',
      company: 'Okta Inc',
      domain: 'your-domain.okta.com',
      support_url: 'https://support.okta.com',
    },
    instructions: `
**Okta Setup Instructions:**

1. Log in to your Okta Admin Console
2. Go to "Applications" → "Applications"
3. Click "Create App Integration"
4. Select "OIDC - OpenID Connect" and "Web Application"
5. Configure the application:
   - Name: Your application name
   - Sign-in redirect URI: (auto-generated above - copy it!)
   - Sign-out redirect URIs: \`http://localhost:3000/login\`
   - Assignments: Choose who can access this app
6. Copy the Client ID and Client Secret
7. Update the well-known URL with your Okta domain

**Replace "your-domain" with your actual Okta domain.**
**Example:** \`https://dev-123456.okta.com/.well-known/openid-configuration\`
    `.trim(),
  },

  github: {
    provider_name: 'github',
    display_name: 'Sign in with GitHub',
    logo_url: '/assets/logos/github-logo.svg',
    openid_wellknown_url: '', // GitHub doesn't use OpenID Connect
    scope: 'user:email',
    attribute_mapping: {
      email: 'email',
      given_name: 'name',
      family_name: '',
      picture: 'avatar_url',
      sub: 'id',
    },
    instructions: `
**GitHub OAuth Setup Instructions:**

1. Go to GitHub → Settings → Developer settings → OAuth Apps
2. Click "New OAuth App"
3. Fill in the application details:
   - Application name: Your app name
   - Homepage URL: \`http://localhost:3000\`
   - Authorization callback URL: (auto-generated above - copy it!)
4. Click "Register application"
5. Copy the Client ID
6. Generate a new client secret and copy it

**Note:** GitHub uses OAuth 2.0, not OpenID Connect
**Leave the well-known URL empty for GitHub**
    `.trim(),
  },

  linkedin: {
    provider_name: 'linkedin',
    display_name: 'Sign in with LinkedIn',
    logo_url: '/assets/logos/linkedin-logo.svg',
    openid_wellknown_url: '', // LinkedIn doesn't use OpenID Connect
    scope: 'r_liteprofile r_emailaddress',
    attribute_mapping: {
      email: 'emailAddress',
      given_name: 'firstName',
      family_name: 'lastName',
      picture: 'profilePicture',
      sub: 'id',
    },
    instructions: `
**LinkedIn OAuth Setup Instructions:**

1. Go to [LinkedIn Developer Portal](https://developer.linkedin.com/)
2. Click "Create App"
3. Fill in the required information:
   - App name: Your application name
   - LinkedIn Page: Associate with a LinkedIn page
   - Privacy policy URL: Your privacy policy URL
   - App logo: Upload your logo
4. In the "Auth" tab, add redirect URLs:
   - \`http://localhost:5011/api/auth/sso/linkedin/callback\`
5. In the "Products" tab, request access to "Sign In with LinkedIn"
6. Copy the Client ID and Client Secret from the "Auth" tab

**Required Scopes:** r_liteprofile, r_emailaddress
**Note:** LinkedIn uses OAuth 2.0, not OpenID Connect
    `.trim(),
  },

  facebook: {
    provider_name: 'facebook',
    display_name: 'Sign in with Facebook',
    logo_url: '/assets/logos/facebook-logo.svg',
    openid_wellknown_url: '', // Facebook doesn't use OpenID Connect
    scope: 'email public_profile',
    attribute_mapping: {
      email: 'email',
      given_name: 'first_name',
      family_name: 'last_name',
      picture: 'picture.data.url',
      sub: 'id',
    },
    instructions: `
**Facebook OAuth Setup Instructions:**

1. Go to [Facebook Developers](https://developers.facebook.com/)
2. Click "Create App" → "Consumer" → "Next"
3. Enter your app display name and contact email
4. Go to "Add Product" and add "Facebook Login"
5. In Facebook Login settings:
   - Valid OAuth Redirect URIs: \`http://localhost:5011/api/auth/sso/facebook/callback\`
6. Go to Settings → Basic to get your App ID and App Secret
7. Make sure your app is in "Development" mode for testing

**Required Permissions:** email, public_profile
**Note:** Facebook uses OAuth 2.0, not OpenID Connect
**Additional setup may be required for production apps**
    `.trim(),
  },

  auth0: {
    provider_name: 'auth0',
    display_name: 'Sign in with Auth0',
    logo_url: '/assets/logos/auth0-logo.svg',
    openid_wellknown_url: 'https://your-domain.auth0.com/.well-known/openid-configuration',
    scope: 'openid profile email',
    attribute_mapping: defaultAttributeMapping,
    instructions: `
**Auth0 Setup Instructions:**

1. Log in to your [Auth0 Dashboard](https://manage.auth0.com/)
2. Go to "Applications" → "Create Application"
3. Choose "Regular Web Applications"
4. Go to the "Settings" tab of your application
5. Configure the following:
   - Allowed Callback URLs: \`http://localhost:5011/api/auth/sso/auth0/callback\`
   - Allowed Logout URLs: \`http://localhost:3000/login\`
   - Allowed Web Origins: \`http://localhost:3000\`
6. Copy the Domain, Client ID, and Client Secret
7. Update the well-known URL with your Auth0 domain

**Replace "your-domain" with your Auth0 domain**
**Example:** \`https://dev-abc123.us.auth0.com/.well-known/openid-configuration\`
    `.trim(),
  },

  keycloak: {
    provider_name: 'keycloak',
    display_name: 'Sign in with Keycloak',
    logo_url: '/assets/logos/keycloak-logo.svg',
    openid_wellknown_url: 'https://your-keycloak-server/auth/realms/your-realm/.well-known/openid-configuration',
    scope: 'openid profile email',
    attribute_mapping: defaultAttributeMapping,
    instructions: `
**Keycloak Setup Instructions:**

1. Log in to your Keycloak Admin Console
2. Select your realm (or create a new one)
3. Go to "Clients" → "Create"
4. Configure the client:
   - Client ID: Your application identifier
   - Client Protocol: openid-connect
   - Root URL: \`http://localhost:3000\`
5. In the client settings:
   - Access Type: confidential
   - Valid Redirect URIs: \`http://localhost:5011/api/auth/sso/keycloak/callback\`
   - Web Origins: \`http://localhost:3000\`
6. Go to the "Credentials" tab and copy the secret
7. Update the well-known URL with your Keycloak server and realm

**URL Format:** \`https://your-server/auth/realms/your-realm/.well-known/openid-configuration\`
    `.trim(),
  },
};

// Helper function to get available provider templates
export const getProviderTemplateNames = (): string[] => {
  return Object.keys(providerTemplates);
};

// Helper function to get a specific provider template
export const getProviderTemplate = (providerName: string): ProviderTemplate | undefined => {
  return providerTemplates[providerName];
};

// Helper function to check if a provider is OAuth 2.0 only (not OpenID Connect)
export const isOAuth2OnlyProvider = (providerName: string): boolean => {
  const oauth2Providers = ['github', 'linkedin', 'facebook'];
  return oauth2Providers.includes(providerName);
};

// Helper function to get provider logo URL
export const getProviderLogoUrl = (providerName: string): string => {
  const template = getProviderTemplate(providerName);
  return template?.logo_url || `/assets/logos/${providerName}-logo.svg`;
};

// Default form values for new providers
export const getDefaultProviderData = () => ({
  provider_name: '',
  display_name: '',
  logo_url: '',
  openid_wellknown_url: '',
  client_id: '',
  client_secret: '',
  redirect_uri: '',
  scope: 'openid profile email',
  response_type: 'code',
  enabled: true,
  priority: 1,
  allow_signup: true,
  link_existing_accounts: true,
  attribute_mapping: defaultAttributeMapping,
  additional_params: {},
  metadata: {},
});